import { FFTheme } from '../../../../assets/theme';
import { sidebar } from '../../../../assets/theme/tokens';

type OwnerState = {
  active: string;
  color: string;
  transparentSidenav: boolean;
  whiteSidenav: boolean;
  darkMode: boolean;
  miniSidenav?: boolean;
  name?: string | React.ReactNode;
  noCollapse?: boolean;
  open?: boolean;
}

function item(theme: any, ownerState: OwnerState) {
  // should be FFTheme
  const { palette, borders, functions, transitions } = theme;
  const { active, color, transparentSidenav, whiteSidenav, darkMode } =
    ownerState;

  const { transparent, white, grey } = palette as any;
  const { borderRadius } = borders;
  const { rgba } = functions;

  return {
    pl: '15px',
    mt: 0.375,
    mb: 0.3,
    width: '100%',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    backgroundColor: () => {
      let backgroundValue = transparent.main;

      if (
        (active === 'isParent' && !transparentSidenav && !whiteSidenav) ||
        (active === 'isParent' && transparentSidenav && darkMode)
      ) {
        backgroundValue = rgba(white.main, 0.2);
      } else if (active === 'isParent' && transparentSidenav) {
        backgroundValue = grey[300];
      } else if (active === 'isParent' && whiteSidenav) {
        backgroundValue = grey[200];
      } else if (active && whiteSidenav) {
        // Steerage sidebar: a light indigo tint, not a solid block.
        backgroundValue = sidebar.linkActiveBg;
      } else if (active) {
        backgroundValue = palette[color].main;
      }

      return backgroundValue;
    },
    transition: transitions.create('background-color', {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.sharp,
    }),

    '&:hover, &:focus': {
      backgroundColor:
        !active &&
        (whiteSidenav
          ? sidebar.linkHoverBg
          : rgba(transparentSidenav && !darkMode ? grey[400] : white.main, 0.2)),
    },
  };
}

function itemContent(theme: FFTheme, ownerState: OwnerState) {
  const { palette, typography, transitions, functions } = theme;
  const {
    miniSidenav,
    name,
    active,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = ownerState;

  const { white, dark } = palette as any;
  const { size, fontWeightRegular, fontWeightLight } = typography as any;
  const { pxToRem } = functions;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Width net of the indent, so the row's tint always reaches past the text.
    width: `calc(100% - ${pxToRem(18)})`,
    minWidth: 0,
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    marginLeft: pxToRem(18),
    userSelect: 'none',
    position: 'relative',

    '& span': {
      color: whiteSidenav
        ? active && active !== 'isParent' ? sidebar.linkActiveColor : sidebar.linkColor
        : transparentSidenav && !darkMode && (active === 'isParent' || !active)
          ? dark.main
          : white.main,
      fontWeight: active ? fontWeightRegular : fontWeightLight,
      fontSize: whiteSidenav ? sidebar.fontSize : size.xs,
      // A label too long for the row ends in an ellipsis instead of spilling out.
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      opacity: miniSidenav ? 0 : 1,
      transition: transitions.create(['opacity', 'color'], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },
  };
}

function itemArrow(theme: FFTheme, ownerState: OwnerState) {
  const { palette, typography, transitions, breakpoints, functions } = theme;
  const {
    noCollapse,
    transparentSidenav,
    whiteSidenav,
    miniSidenav,
    open,
    active,
    darkMode,
  } = ownerState;

  const { white, dark } = palette as any;
  const { size } = typography as any;
  const { pxToRem, rgba } = functions;

  return {
    fontSize: `${size.lg} !important`,
    fontWeight: 700,
    marginBottom: pxToRem(-1),
    transform: open ? 'rotate(0)' : 'rotate(-180deg)',
    color: () => {
      let colorValue;

      if (transparentSidenav && darkMode) {
        colorValue = open || active ? white.main : rgba(white.main, 0.25);
      } else if (transparentSidenav || whiteSidenav) {
        colorValue = open || active ? dark.main : rgba(dark.main, 0.25);
      } else {
        colorValue = open || active ? white.main : rgba(white.main, 0.5);
      }

      return colorValue;
    },
    transition: transitions.create(['color', 'transform', 'opacity'], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.shorter,
    }),

    [breakpoints.up('xl')]: {
      display:
        noCollapse || (transparentSidenav && miniSidenav) || miniSidenav
          ? 'none !important'
          : 'block !important',
    },
  };
}

export { item, itemContent, itemArrow };
